import axios from 'axios';
import * as Sentry from '@sentry/react';

const errorResponse = (err) => {
  // Enhanced error logging for network issues
  const errorInfo = {
    message: err.message,
    code: err.code,
    status: err.response?.status,
    statusText: err.response?.statusText,
    data: err.response?.data,
    headers: err.response?.headers,
    config: {
      url: err.config?.url,
      method: err.config?.method,
      timeout: err.config?.timeout,
      headers: err.config?.headers,
    },
    // Network-specific error details
    isNetworkError: !err.response && !err.request,
    isTimeout: err.code === 'ECONNABORTED',
    isConnectionRefused: err.code === 'ECONNREFUSED',
    isDNSFailure: err.code === 'ENOTFOUND',
    isNetworkUnavailable: err.code === 'ERR_NETWORK',
    requestInfo: err.request
      ? {
          readyState: err.request.readyState,
          status: err.request.status,
          responseURL: err.request.responseURL,
        }
      : null,
  };

  // Log network errors with enhanced details
  if (
    errorInfo.isNetworkError ||
    errorInfo.isTimeout ||
    errorInfo.isConnectionRefused ||
    errorInfo.isDNSFailure ||
    errorInfo.isNetworkUnavailable
  ) {
    Sentry.captureMessage('Network error detected', {
      level: 'error',
      extra: errorInfo,
      tags: {
        errorType: 'network',
        errorCode: err.code || 'unknown',
      },
    });
  }

  if (err.response?.data) {
    return Promise.reject(err.response.data);
  } else {
    return Promise.reject(err);
  }
};

export const adminClient = axios.create({
  baseURL: process.env.REACT_ADMIN_API,
});

const sosClient = axios.create({
  baseURL: '/api',
});

// Add request interceptor to log outgoing requests
sosClient.interceptors.request.use(
  (config) => {
    Sentry.addBreadcrumb({
      category: 'http',
      message: `Outgoing ${config.method?.toUpperCase()} request`,
      level: 'info',
      data: {
        url: config.url,
        method: config.method,
        baseURL: config.baseURL,
        timeout: config.timeout,
        hasData: !!config.data,
        dataSize: config.data ? JSON.stringify(config.data).length : 0,
      },
    });
    return config;
  },
  (error) => {
    Sentry.captureException(error, {
      tags: { function: 'requestInterceptor' },
    });
    return Promise.reject(error);
  }
);

// Add response interceptor to log successful responses
sosClient.interceptors.response.use((response) => {
  Sentry.addBreadcrumb({
    category: 'http',
    message: `Received ${response.status} response`,
    level: 'info',
    data: {
      url: response.config.url,
      method: response.config.method,
      status: response.status,
      statusText: response.statusText,
      responseSize: response.data ? JSON.stringify(response.data).length : 0,
    },
  });
  return response.data;
}, errorResponse);

adminClient.interceptors.response.use((resp) => resp.data, errorResponse);

export const uploadImage = async (
  retry,
  file,
  fileKey,
  folderName,
  onUploadProgress
) => {
  const maxRetries = 3;

  let retryCount = 0;
  let key = null;
  let url = null;
  let hasAttemptedDelay = false; // Track if we've already tried the delay

  // Enhanced file object validation function
  const validateFileObject = async (file, fileKey) => {
    console.log('🔍 Starting file object validation for:', fileKey);

    const validationResults = {
      timestamp: Date.now(),
      filename: fileKey,
      fileSize: file?.size,
      fileType: file?.type,
      tests: {},
    };

    try {
      // Test 1: Basic file object integrity
      validationResults.tests.basicIntegrity = {
        exists: !!file,
        isFile: file instanceof File,
        isBlob: file instanceof Blob,
        hasSize: typeof file?.size === 'number',
        hasType: typeof file?.type === 'string',
        hasName: typeof file?.name === 'string',
        hasLastModified: typeof file?.lastModified === 'number',
      };

      console.log(
        '✅ Basic integrity test:',
        validationResults.tests.basicIntegrity
      );

      // Test 2: Can we slice the file?
      try {
        const slice = file.slice(0, 1024);
        validationResults.tests.canSlice = {
          success: true,
          sliceSize: slice.size,
          sliceType: slice.type,
        };
        console.log('✅ Slice test passed:', slice.size, 'bytes');
      } catch (sliceError) {
        validationResults.tests.canSlice = {
          success: false,
          error: sliceError.message,
        };
        console.error('❌ Slice test failed:', sliceError.message);
      }

      // Test 3: Can we read the file content?
      try {
        const reader = new FileReader();
        const readPromise = new Promise((resolve, reject) => {
          reader.onload = (event) => {
            const result = event.target.result;
            resolve({
              success: true,
              bytesRead: result.byteLength,
              hasData: !!result,
            });
          };
          reader.onerror = (event) => {
            const error = event.target.error;
            reject(
              new Error(
                `FileReader error: ${error?.name || 'Unknown'} - ${
                  error?.message || 'No message'
                }`
              )
            );
          };
          reader.onabort = () =>
            reject(new Error('FileReader aborted by user'));
        });

        // Add timeout to prevent hanging
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(
            () => reject(new Error('FileReader timeout after 2 seconds')),
            2000
          );
        });

        const slice = file.slice(0, 1024);
        reader.readAsArrayBuffer(slice);

        const result = await Promise.race([readPromise, timeoutPromise]);

        validationResults.tests.canRead = {
          success: true,
          bytesRead: result.bytesRead,
          hasData: result.hasData,
        };
        console.log('✅ Read test passed:', result.bytesRead, 'bytes read');
      } catch (readError) {
        validationResults.tests.canRead = {
          success: false,
          error: readError.message,
          errorType: readError.name,
          timestamp: Date.now(),
        };
        console.error('❌ Read test failed:', readError.message);

        // Additional diagnostic information
        try {
          const diagnosticSlice = file.slice(0, 1);
          validationResults.tests.canRead.diagnostic = {
            canSlice1Byte: true,
            sliceSize: diagnosticSlice.size,
            sliceType: diagnosticSlice.type,
          };
        } catch (diagnosticError) {
          validationResults.tests.canRead.diagnostic = {
            canSlice1Byte: false,
            error: diagnosticError.message,
          };
        }
      }

      // Test 4: Can we create a new File object from the original?
      try {
        const newFile = new File([file], file.name, { type: file.type });
        validationResults.tests.canRecreate = {
          success: true,
          newFileSize: newFile.size,
          newFileType: newFile.type,
        };
        console.log('✅ Recreate test passed:', newFile.size, 'bytes');
      } catch (recreateError) {
        validationResults.tests.canRecreate = {
          success: false,
          error: recreateError.message,
        };
        console.error('❌ Recreate test failed:', recreateError.message);
      }

      // Test 5: Can we create a Blob from the file?
      try {
        const blob = new Blob([file], { type: file.type });
        validationResults.tests.canCreateBlob = {
          success: true,
          blobSize: blob.size,
          blobType: blob.type,
        };
        console.log('✅ Blob creation test passed:', blob.size, 'bytes');
      } catch (blobError) {
        validationResults.tests.canCreateBlob = {
          success: false,
          error: blobError.message,
        };
        console.error('❌ Blob creation test failed:', blobError.message);
      }

      // Test 6: File health check - additional diagnostics
      try {
        const healthCheck = {
          fileSizeValid: file.size > 0 && file.size < 100 * 1024 * 1024, // 100MB limit
          fileTypeValid: file.type && file.type.startsWith('image/'),
          fileNameValid:
            file.name && file.name.length > 0 && file.name.length < 255,
          lastModifiedValid:
            file.lastModified > 0 && file.lastModified < Date.now() + 86400000, // Within 24 hours of now
          canAccessProperties: {
            size: typeof file.size === 'number',
            type: typeof file.type === 'string',
            name: typeof file.name === 'string',
            lastModified: typeof file.lastModified === 'number',
          },
          memoryUsage: {
            estimatedSize: file.size,
            estimatedMemory: file.size * 1.5, // Rough estimate of memory usage
          },
        };

        validationResults.tests.fileHealth = {
          success:
            healthCheck.fileSizeValid &&
            healthCheck.fileTypeValid &&
            healthCheck.fileNameValid &&
            healthCheck.lastModifiedValid,
          details: healthCheck,
        };

        if (
          healthCheck.fileSizeValid &&
          healthCheck.fileTypeValid &&
          healthCheck.fileNameValid &&
          healthCheck.lastModifiedValid
        ) {
          console.log('✅ File health check passed');
        } else {
          console.warn('⚠️ File health check issues detected:', healthCheck);
        }
      } catch (healthError) {
        validationResults.tests.fileHealth = {
          success: false,
          error: healthError.message,
        };
        console.error('❌ File health check failed:', healthError.message);
      }

      Sentry.addBreadcrumb({
        category: 'upload',
        message: 'File object validation completed',
        level: 'info',
        data: validationResults,
      });

      // If any critical tests fail, log a warning
      const criticalTests = [
        'basicIntegrity',
        'canSlice',
        'canRead',
        'fileHealth',
      ];
      const failedTests = criticalTests.filter((test) => {
        if (test === 'basicIntegrity') {
          // Check if all basic integrity properties are true
          const integrity = validationResults.tests.basicIntegrity;
          return !(
            integrity.exists &&
            integrity.isFile &&
            integrity.isBlob &&
            integrity.hasSize &&
            integrity.hasType &&
            integrity.hasName &&
            integrity.hasLastModified
          );
        } else {
          // For other tests, check if success is false
          return !validationResults.tests[test]?.success;
        }
      });

      if (failedTests.length > 0) {
        console.warn(
          '⚠️ File validation failed for critical tests:',
          failedTests
        );
        console.warn('📊 Full validation results:', validationResults);

        // Provide specific guidance based on which tests failed
        if (failedTests.includes('canRead')) {
          console.warn('🔍 FileReader failure detected. This could indicate:');
          console.warn('   - File corruption or invalid format');
          console.warn('   - Browser memory issues');
          console.warn('   - File permission problems');
          console.warn('   - Network interruption during file selection');
          console.warn('   - File still downloading/processing on device');

          if (validationResults.tests.canRead?.diagnostic) {
            console.warn(
              '   - Diagnostic info:',
              validationResults.tests.canRead.diagnostic
            );
          }
        }

        if (failedTests.includes('fileHealth')) {
          console.warn(
            '🔍 File health issues detected. Check file properties and size limits.'
          );
        }

        Sentry.captureMessage('File object validation failed', {
          level: 'warning',
          extra: {
            failedTests,
            validationResults,
            recommendations: {
              canReadFailure: failedTests.includes('canRead')
                ? 'Consider re-selecting the file or checking file integrity'
                : null,
              fileHealthFailure: failedTests.includes('fileHealth')
                ? 'Check file size, type, and metadata validity'
                : null,
            },
          },
        });
      } else {
        console.log('🎉 All file validation tests passed!');
      }

      return validationResults;
    } catch (validationError) {
      console.error('💥 File validation crashed:', validationError);

      Sentry.captureException(validationError, {
        tags: { function: 'validateFileObject' },
        extra: {
          filename: fileKey,
        },
      });
      return { error: validationError.message };
    }
  };

  // Capture network information if available
  const networkInfo = {
    online: navigator.onLine,
    connectionType: navigator.connection?.effectiveType || 'unknown',
    downlink: navigator.connection?.downlink || 'unknown',
    rtt: navigator.connection?.rtt || 'unknown',
    saveData: navigator.connection?.saveData || false,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
  };

  Sentry.addBreadcrumb({
    category: 'upload',
    message: 'Uploading file',
    level: 'warning',
    data: {
      name: fileKey,
      fileSize: file.size,
      fileType: file.type,
      folder: folderName,
      networkInfo,
    },
  });

  while (retryCount < maxRetries) {
    try {
      const response = await sosClient.post('cart/signed-url', {
        type: file.type,
        name: fileKey,
        folder: folderName,
      });
      key = response.key;
      url = response.url;

      if (!url || !key) {
        throw new Error('Invalid response from signed-url endpoint');
      }

      try {
        // Primary method: Direct upload to S3
        const putResponse = await axios.put(url, file, {
          headers: {
            'Content-Type': file.type,
          },
          onUploadProgress,
        });

        if (putResponse.status >= 200 && putResponse.status < 300) {
          return { key };
        } else {
          throw new Error(`Upload failed with status ${putResponse.status}`);
        }
      } catch (putError) {
        // Fallback method, form data sent to /upload-fallback api using sdk
        Sentry.captureMessage('Direct upload failed, attempting fallback', {
          extra: {
            putError: putError.message,
            putErrorStatus: putError.response?.status,
            putErrorData: putError.response?.data,
          },
        });

        // Run validation before attempting fallback
        console.log('🔄 Running validation before fallback upload...');
        const validationResults = await validateFileObject(file, fileKey);
        console.log('📋 Fallback validation results:', validationResults);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('key', fileKey);
        formData.append('folder', folderName);

        const fallbackResponse = await sosClient.post(
          'cart/upload-fallback',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress,
          }
        );
        if (fallbackResponse && fallbackResponse.success) {
          Sentry.captureMessage('fallback upload successful');
          return { key: fallbackResponse.key };
        } else {
          throw new Error('Fallback upload failed');
        }
      }
    } catch (error) {
      // Check if this is the first failure and we haven't tried the delay yet
      if (!hasAttemptedDelay && retryCount === 0) {
        console.log(
          '⏳ First upload attempt failed, waiting 2 seconds for file to be ready...'
        );
        hasAttemptedDelay = true;

        Sentry.addBreadcrumb({
          category: 'upload',
          message: 'Adding 2-second delay after first upload failure',
          level: 'info',
          data: {
            error: error.message,
            retryCount,
            fileKey,
          },
        });

        // Wait 2 seconds before retrying
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Try the same upload method again without incrementing retryCount
        continue;
      }

      // Emergency fallback: Save to server when both S3 methods fail
      try {
        Sentry.captureMessage(
          'Both S3 upload methods failed, attempting emergency server storage',
          {
            extra: {
              originalError: error.message,
              originalErrorStatus: error.response?.status,
              originalErrorData: error.response?.data,
            },
          }
        );

        // Enhanced debugging: Test emergency upload preparation
        Sentry.addBreadcrumb({
          category: 'upload',
          message: 'Preparing emergency upload',
          level: 'info',
          data: {
            retryCount,
            fileSize: file.size,
            fileType: file.type,
          },
        });

        // Run validation before emergency upload as well
        console.log('🚨 Running validation before emergency upload...');
        const emergencyValidationResults = await validateFileObject(
          file,
          fileKey
        );
        console.log(
          '📋 Emergency validation results:',
          emergencyValidationResults
        );

        // Check if file reader test failed and create a new file from blob if needed
        let fileToUpload = file;
        if (emergencyValidationResults.tests?.canRead?.success === false) {
          console.log(
            '🔄 File reader test failed, creating new file from blob...'
          );
          try {
            // Create a blob from the original file
            const blob = new Blob([file], { type: file.type });

            // Create a new File object from the blob
            const newFile = new File([blob], file.name, {
              type: file.type,
              lastModified: file.lastModified,
            });

            console.log('✅ Successfully created new file from blob:', {
              originalSize: file.size,
              newFileSize: newFile.size,
              originalType: file.type,
              newFileType: newFile.type,
            });

            fileToUpload = newFile;

            Sentry.addBreadcrumb({
              category: 'upload',
              message: 'Created new file from blob due to FileReader failure',
              level: 'info',
              data: {
                originalSize: file.size,
                newFileSize: newFile.size,
                originalType: file.type,
                newFileType: newFile.type,
                canReadTestFailed: true,
              },
            });
          } catch (blobError) {
            console.error(
              '❌ Failed to create new file from blob:',
              blobError.message
            );
            Sentry.captureMessage('Failed to create new file from blob', {
              level: 'error',
              extra: {
                blobError: blobError.message,
                originalFileSize: file.size,
                originalFileType: file.type,
              },
            });
            // Continue with original file if blob creation fails
            fileToUpload = file;
          }
        }

        const formData = new FormData();
        formData.append('file', fileToUpload);
        formData.append('key', fileKey);
        formData.append('folder', folderName);

        const emergencyResponse = await sosClient.post(
          'cart/upload-emergency',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress,
          }
        );

        if (emergencyResponse && emergencyResponse.success) {
          Sentry.captureMessage('Emergency server storage successful');
          return {
            key: emergencyResponse.key,
            fileSizeMB: emergencyResponse.fileSizeMB,
            canAttach: emergencyResponse.canAttach,
            downloadUrl: emergencyResponse.downloadUrl,
            emergency: true,
          };
        } else {
          Sentry.captureMessage('Emergency upload failed');
          throw new Error('Emergency upload failed');
        }
      } catch (emergencyError) {
        // If emergency upload also fails, continue with normal retry logic
        retryCount++;

        // Enhanced logging for files when they fail
        const errorData = {
          originalErrorMessage: error.message,
          emergencyErrorMessage: emergencyError.message,
          emergencyErrorResponse: emergencyError.response?.data,
          emergencyErrorStatus: emergencyError.response?.status,
          emergencyErrorHeaders: emergencyError.response?.headers,
          fileSize: file.size,
          fileType: file.type,
        };

        Sentry.addBreadcrumb({
          category: 'upload',
          message: `Upload attempt ${retryCount} failed - all three methods failed`,
          level: 'error',
          data: errorData,
        });

        Sentry.captureException(emergencyError, {
          tags: {
            function: 'uploadImage',
          },
          extra: {
            retryCount,
            fileKey,
            folderName,
            errorMessage: error.message,
            emergencyErrorMessage: emergencyError.message,
            errorResponse: emergencyError.response?.data,
            networkInfo,
            // Enhanced network error details
            networkErrorDetails: {
              isNetworkError:
                !emergencyError.response && !emergencyError.request,
              isTimeout: emergencyError.code === 'ECONNABORTED',
              isConnectionRefused: emergencyError.code === 'ECONNREFUSED',
              isDNSFailure: emergencyError.code === 'ENOTFOUND',
              isNetworkUnavailable: emergencyError.code === 'ERR_NETWORK',
              errorCode: emergencyError.code,
              errorMessage: emergencyError.message,
              requestInfo: emergencyError.request
                ? {
                    readyState: emergencyError.request.readyState,
                    status: emergencyError.request.status,
                    responseURL: emergencyError.request.responseURL,
                  }
                : null,
              configInfo: emergencyError.config
                ? {
                    url: emergencyError.config.url,
                    method: emergencyError.config.method,
                    timeout: emergencyError.config.timeout,
                    headers: emergencyError.config.headers,
                  }
                : null,
            },
          },
        });

        if (retryCount === maxRetries) {
          // If retry is false, throw an error to stop the upload
          if (!retry) {
            throw new Error(
              `Failed to upload after ${maxRetries} attempts: ${emergencyError.message}`
            );
          }
          // If retry is true, return the key to the caller
          Sentry.captureMessage(
            'Upload failed after retry click, skipping upload, image will be missing'
          );
          return { key };
        }

        // Exponential backoff
        const backoffTime = 100 * Math.pow(2, retryCount);
        await new Promise((resolve) => setTimeout(resolve, backoffTime));
      }
    }
  }
};

export const calculatePrice = (data) => {
  return sosClient.post('cart/price', data);
};

export const createCart = (data) => sosClient.post('cart', data);

export const createOrder = (data) => sosClient.post('orders', data);

export const parseAddress = (text) =>
  axios
    .post(
      'https://qjn81caq90.execute-api.us-west-2.amazonaws.com/prod/parse-address',
      text
    )
    .then((resp) => resp.data);

export const recoverAccountId = (email) =>
  sosClient.post('cart/recover-account', { email });

// Utility function to check if a file object is likely stale
export const isFileStale = (file) => {
  try {
    const fileAge = Date.now() - file.lastModified;
    return fileAge > 300000; // 5 minutes
  } catch (error) {
    console.warn('⚠️ Could not check file staleness:', error.message);
    return false;
  }
};
